import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Chip, Tooltip } from "@mui/material";
import { useStoreState } from 'pullstate';
import { useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import Loader from './components/Loader.jsx';
import SnackbarAlert from './components/SnackbarAlert.jsx';
import SlackCallback from "./hooks/SlackCallback.jsx";
import AccessType from "./pages/accessManager/AccessType.jsx";
import CheckInForm from "./pages/checkins/CheckInForm.jsx";
import CliftonStrengths from "./pages/cliftonStrengths/CliftonStrengths.jsx";
import Departments from "./pages/department/Departments.jsx";
import EmployeeProfile from "./pages/employee/EmployeeProfile.jsx";
import EmployeeList from "./pages/employee/EmployeesList.jsx";
import GoalForm from "./pages/goals/GoalForm.jsx";
import EmployeesIframe from "./pages/iframe/EmployeesIframe.jsx";
import GoogleAuth from "./pages/login/GoogleAuth.jsx";
import SlackAuth from "./pages/login/SlackAuth.jsx";
import Navbar from "./pages/navbar/Navbar.jsx";
import NotAuth from "./pages/notAuth/NotAuth.jsx";
import DepartmentPeopleBuilder from "./pages/peopleBuilder/DepartmentPeopleBuilder.jsx";
import PeopleBuilder from "./pages/peopleBuilder/PeopleBuilder.jsx";
import PeopleBuilderSettings from "./pages/peopleBuilderSettings/PeopleBuilderSettings.jsx";
import RecentGoals from "./pages/recentGoals/RecentGoals.jsx";
import Swagger from "./pages/swagger/Swagger.jsx";
import Titles from "./pages/title/Titles.jsx";
import WorkLocations from "./pages/workLocation/WorkLocations.jsx";
import { AppStore } from './stores/AppStore.js';
import { api } from './utils/apiFunctions.js';
import { getApiEnvironment, getCheckInGoals, getCheckIns, getDepartments, getEmployees, getGoals, getPbSettings, getPeopleBuilders, getSnackbar, getStrengths } from './utils/getData.js';
import { restoreAuthentication } from './utils/restoreAuth.js';

const App = () => {

    const { googleToken, slackUserData, user } = useStoreState(AppStore)
    const [loading, setLoading] = useState(true);
    const isAuthenticated = googleToken && slackUserData ? true : false

    // detect if the app is running in an iframe
    const isIframe = window.location.pathname.startsWith('/iframe');

    const checkToken = () => {
        if (!slackUserData) {
            return <SlackAuth />;
        }
    };

    useEffect(() => {
        checkToken();
    }, [slackUserData]);

    useEffect(() => {
        restoreAuthentication(api, setLoading);
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;

        const initializationFunctions = async () => {
            try {
                AppStore.update((s) => { s.loading = true });

                await getApiEnvironment();
                await getEmployees();
                await getStrengths();
                await getSnackbar();
                await getPeopleBuilders();
                await getGoals();
                await getCheckIns();
                await getCheckInGoals();
                await getDepartments();
                await getPbSettings();

            } catch (error) {
                console.error('Error in initialization:', error);
            } finally {
                AppStore.update((s) => { s.loading = false });
            }
        };

        initializationFunctions();
    }, [isAuthenticated]);

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            {!isIframe && <SnackbarAlert />}
            <Router>
                {isAuthenticated && !isIframe && <Navbar />}
                <Routes>
                    {/* IFRAME ROUTE - NOT AUTHENTICATED */}
                    <Route path="/iframe/employees" element={<EmployeesIframe />} />

                    {/* AUTH ROUTES */}
                    <Route path="/slack-auth" element={<SlackAuth />} />
                    <Route path="/not-auth" element={<NotAuth />} />
                    <Route path="/slack-code" element={<SlackCallback />} />

                    {/* LOGGED IN ROUTES */}
                    <Route
                        path="/work-locations"
                        element={isAuthenticated ? <WorkLocations /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/employee/profile/:userId"
                        element={isAuthenticated ? <EmployeeProfile /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/departments"
                        element={isAuthenticated ? <Departments /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/recent-goals"
                        element={isAuthenticated ? <RecentGoals /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/clifton-strengths"
                        element={isAuthenticated ? <CliftonStrengths /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/titles"
                        element={isAuthenticated ? <Titles /> : <Navigate to="/" />}
                    />
                    <Route
                        path={`/api-docs/`}
                        element={isAuthenticated ? <Swagger email={user.email} /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/goal-form/people-builder/:peopleBuilderId?"
                        element={isAuthenticated ? <GoalForm /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/goal-form/people-builder/:peopleBuilderId/goal/:goalId"
                        element={isAuthenticated ? <GoalForm /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/people-builder-settings"
                        element={isAuthenticated ? <PeopleBuilderSettings /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/personal-people-builder/:userId?"
                        element={isAuthenticated ? <PeopleBuilder /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/team-people-builder/:userId?"
                        element={isAuthenticated ? <PeopleBuilder /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/company-people-builder/:userId"
                        element={isAuthenticated ? <PeopleBuilder /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/department-people-builder"
                        element={isAuthenticated ? <DepartmentPeopleBuilder /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/new-check-in/:playerId?/:peopleBuilderId?"
                        element={isAuthenticated ? <CheckInForm /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/new-mid-check-in/:playerId?/:peopleBuilderId?"
                        element={isAuthenticated ? <CheckInForm /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/new-end-check-in/:playerId?/:peopleBuilderId?"
                        element={isAuthenticated ? <CheckInForm /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/check-in/:playerId?/:checkinId?/:peopleBuilderId?"
                        element={isAuthenticated ? <CheckInForm /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/mid-year-check-in/:checkinId?/:peopleBuilderId?"
                        element={isAuthenticated ? <CheckInForm /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/end-year-check-in/:checkinId?/:peopleBuilderId?"
                        element={isAuthenticated ? <CheckInForm /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/employee"
                        element={isAuthenticated ? <EmployeeList /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/access-type"
                        element={isAuthenticated ? <AccessType /> : <Navigate to="/" />}
                    />
                    <Route
                        path="/"
                        element={isAuthenticated ? <Navigate to="/employee" /> : <GoogleAuth />}
                    />
                    <Route path="/*" element={<Navigate to="/" />} />
                </Routes>
            </Router>
            {!isIframe && (
                <Tooltip title="Help" arrow placement="left">
                    <Chip
                        color="primary"
                        aria-label="help"
                        sx={{
                            position: "fixed",
                            bottom: 100,
                            right: -10,
                            cursor: "pointer",
                        }}
                        icon={
                            <HelpOutlineIcon
                                onClick={() => window.open('https://etroconstruction.zohodesk.com/portal/en/kb/articles/people-builder-manual')} />
                        }
                    >
                    </Chip >
                </Tooltip>
            )}
        </>
    );
}

export default App;